import { BarChart, Box, Button, Flex, Loading, Text } from 'ui'
import type React from 'react'
import { useMemo } from 'react'
import { useSpendTimeseries } from 'queries/pulse/useSpendTimeseries'
import { usePulseContext } from '../../context/PulseProvider'
import { formatCurrency } from 'lib/string'
import { groupBy, uniq } from 'lodash'
import { formatTimePeriodDate, getColorScale } from 'pages/PulsePage/utils'
import { useSpendTimeseriesDownloadCsv } from 'queries/pulse/useSpendTimeseriesDownloadCsv'
import { LabelPosition } from 'recharts/types/component/Label'
import { LabelList } from 'recharts'

export function SpendTimeseries() {
  const {
    state: { filters },
  } = usePulseContext()

  const { data, isLoading } = useSpendTimeseries(filters)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    useSpendTimeseriesDownloadCsv(filters)

  const chartData = useMemo(() => {
    const groupedByDate = groupBy(data ?? [], 'revenue_date_pst')
    return Object.entries(groupedByDate).map(([date, items]) => {
      return {
        revenue_date_pst: formatTimePeriodDate(filters.timePeriod, date),
        ...items.reduce(
          (acc, item) => {
            acc[item.spend_category] = item.adj_gross_revenue
            acc.total += item.adj_gross_revenue
            return acc
          },
          { total: 0 } as Record<string, number>
        ),
      }
    })
  }, [data])

  const chartSeries = useMemo(() => {
    const negativeValueCategories = ['Reimbursement', 'Other Discount']
    const spendCategories = uniq(data?.map((item) => item.spend_category) ?? [])

    // find the last category that is not a negative value category
    const lastCategoryIndex = spendCategories.findLastIndex(
      (spendCategory) => !negativeValueCategories.includes(spendCategory)
    )

    return spendCategories.map((spendCategory, idx) => {
      let position: LabelPosition = 'center'
      if (negativeValueCategories.includes(spendCategory)) {
        position = 'top' // top becomes bottom for negative values series
      }

      let children: React.ReactNode | undefined = undefined
      if (idx === lastCategoryIndex) {
        children = (
          <LabelList
            dataKey="total"
            position="top"
            formatter={formatCurrency}
            fontSize={12}
          />
        )
      }

      return {
        name: spendCategory,
        label: spendCategory,
        color: getColorScale(spendCategories.length)(idx),
        labelPosition: position,
        labelColor: 'black',
        yAxisId: 'left',
        stackId: 'a',
        children,
      }
    })
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Spend Data Breakdown
          </Text>
          <Text fontSize={2} color="lightText">
            Breakdown of spend by category
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <BarChart
          data={chartData}
          dataKey="revenue_date_pst"
          height={450}
          series={chartSeries}
          valueFormatter={formatCurrency}
          withTooltip
          withLegend
          withXAxis
          withYAxis
          stackOffset="sign"
          yAxisProps={{
            width: 110,
          }}
        />
      )}
    </Box>
  )
}
